import { Component, ElementRef, viewChild, input, effect, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { measureWidestXLabel, truncateSvgText } from '../../../helpers/graphs/svg-text.helper';

// Minimum room between neighbouring x-axis labels before they are shortened
const X_LABEL_GAP = 8;
const X_LABEL_MIN_WIDTH = 14;
// Breathing room between a centred edge label and the plot edge
const X_LABEL_EDGE_GAP = 2;

export interface AreaLineChartDataPoint {
  [key: string]: string | number | Date;
}

export interface AreaLineChartSeries {
  key: string;
  color: string;
  label?: string;
  fillOpacity?: number;
  lineType?: 'curved' | 'straight';  // 'curved' is default (curveMonotoneX), 'straight' is linear
  showInTooltip?: boolean;  // default true, set to false to hide from tooltip
  showLine?: boolean;  // default true, set to false to hide line
}

export interface AreaLineChartFillBetween {
  topKey: string;       // Key for the top boundary
  bottomKey: string;    // Key for the bottom boundary
  color: string;        // Fill color
  fillOpacity?: number; // Fill opacity (default 0.3)
  label?: string;       // Label for legend
}

export interface AreaLineChartLegendItem {
  legend: string;
  color: string;
}

@Component({
  selector: 'app-area-line-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './area-line-chart.component.html',
  styleUrl: './area-line-chart.component.scss'
})
export class AreaLineChartComponent {
  readonly chartContainer = viewChild<ElementRef>('chart');

  public data = input<AreaLineChartDataPoint[]>([]);
  public series = input<AreaLineChartSeries[]>([]);
  public legend = input<AreaLineChartLegendItem[]>([]);
  public xAxisKey = input<string>('x');
  public chartHeight = input<number>(175);
  public yTicks = input<number[]>([100, 80, 60, 40, 20, 0]);
  public xPadding = input<number>(0.2);
  public xAxisFormat = input<((value: any) => string) | undefined>(undefined);
  public yAxisFormat = input<((value: any) => string) | undefined>(undefined);
  public margin = input({ top: 20, right: 10, bottom: 30, left: 0 });
  public tooltipFormat = input<((value: any) => string) | undefined>(undefined);
  public showFill = input<boolean>(true);
  public showDots = input<boolean>(true);
  public fillBetween = input<AreaLineChartFillBetween | undefined>(undefined);
  public xPaddingLeft = input<number | undefined>(undefined);  // Override left padding specifically
  public xPaddingRight = input<number | undefined>(undefined); // Override right padding specifically

  constructor() {
    effect(() => {
      const _data = this.data();
      const _container = this.chartContainer();
      const _series = this.series();

      if (_data && _container?.nativeElement && _series && _series.length > 0) {
        this.createChart();
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.createChart();
  }

  private createChart(): void {
    const _data = this.data();
    const _series = this.series();
    const _xAxisKey = this.xAxisKey();
    const _chartHeight = this.chartHeight();
    const _margin = this.margin();
    const _yTicks = this.yTicks();
    const _tooltipFormat = this.tooltipFormat();
    const _showFill = this.showFill();
    const _showDots = this.showDots();

    if (!_data || _data.length === 0 || !_series || _series.length === 0) {
      return;
    }

    const element = this.chartContainer()?.nativeElement;
    if (!element) return;

    const boundingRect = element.getBoundingClientRect();
    const availableWidth = boundingRect.width - _margin.left - _margin.right;
    const height = _chartHeight - _margin.top - _margin.bottom;

    // Clear existing chart
    d3.select(element).select('svg').remove();
    d3.select(element).select('.tooltip').remove();

    // Create temporary SVG to measure y-axis labels
    const tempSvg = d3.select(element)
      .append('svg')
      .attr('width', availableWidth)
      .attr('height', _chartHeight);

    const tempPlot = tempSvg.append('g')
      .attr('transform', `translate(${_margin.left},${_margin.top})`);

    const _yAxisFormat = this.yAxisFormat();
    const yDomain = [0, Math.max(..._yTicks)];
    const tempYAxisWithFormat = _yAxisFormat
      ? d3.axisLeft(d3.scaleLinear().domain(yDomain).range([height, 0])).tickValues(_yTicks).tickFormat(_yAxisFormat as any)
      : d3.axisLeft(d3.scaleLinear().domain(yDomain).range([height, 0])).tickValues(_yTicks);
    const tempYAxis = tempPlot.append('g').call(tempYAxisWithFormat);
    const maxLabelWidth = Math.ceil(
      Math.max(0, ...(tempYAxis.selectAll('text').nodes() as any[]).map((n: any) => n.getBBox().width))
    );
    const leftPad = maxLabelWidth + 10;
    const width = Math.max(0, availableWidth - leftPad);

    tempSvg.remove();

    // Create tooltip
    const tooltip = d3.select(element)
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('background', 'white')
      .style('border', '1px solid #E6E6E6')
      .style('border-radius', '8px')
      .style('padding', '8px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('z-index', '1000')
      .style('font-size', '12px')
      .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)');

    // Create final SVG
    const svg = d3.select(element)
      .append('svg')
      .attr('width', availableWidth)
      .attr('height', _chartHeight)
      .append('g')
      .attr('transform', `translate(${leftPad},${_margin.top})`);

    // Create scales with configurable padding
    const _xPadding = this.xPadding();
    const _xPaddingLeft = this.xPaddingLeft();
    const _xPaddingRight = this.xPaddingRight();

    // Calculate range with specific left/right padding if provided
    let rangeStart = 0;
    let rangeEnd = width;
    if (_xPaddingLeft !== undefined || _xPaddingRight !== undefined) {
      const paddingLeft = _xPaddingLeft ?? _xPadding;
      const paddingRight = _xPaddingRight ?? _xPadding;
      const step = width / (_data.length - 1 + paddingLeft + paddingRight);
      rangeStart = step * paddingLeft;
      rangeEnd = width - step * paddingRight;
    }

    const x = d3.scalePoint()
      .domain(_data.map(d => String(d[_xAxisKey])))
      .range([rangeStart, rangeEnd])
      .padding(_xPaddingLeft !== undefined || _xPaddingRight !== undefined ? 0 : _xPadding);

    // Labels are centred under their points, so the end points need half the widest label
    // inside the plot; inset them when the configured padding leaves less room than that.
    const inset = Math.ceil(measureWidestXLabel(svg, x) / 2) + X_LABEL_EDGE_GAP;
    const [firstPoint, lastPoint] = [x(x.domain()[0]) ?? 0, x(x.domain()[x.domain().length - 1]) ?? width];
    if (firstPoint < inset || lastPoint > width - inset) {
      x.range([Math.max(rangeStart, inset), Math.min(rangeEnd, width - inset)]).padding(0);
    }

    const yDomainMax = Math.max(..._yTicks);
    const y = d3.scaleLinear()
      .domain([0, yDomainMax])
      .range([height, 0]);

    // Add y-axis with horizontal gridlines
    const yAxisWithFormat = _yAxisFormat
      ? d3.axisLeft(y).tickValues(_yTicks).tickFormat(_yAxisFormat as any).tickSize(-width).tickPadding(10)
      : d3.axisLeft(y).tickValues(_yTicks).tickSize(-width).tickPadding(10);

    const yAxisGroup = svg.append('g')
      .attr('class', 'y-axis')
      .call(yAxisWithFormat);

    yAxisGroup.select('.domain').remove();

    yAxisGroup.selectAll('line')
      .style('stroke', '#E0E0E0');

    yAxisGroup.selectAll('text')
      .attr('text-anchor', 'end')
      .style('font-family', '"Nunito Sans", sans-serif')
      .style('font-size', '10px')
      .style('fill', '#9FA1A4');

    // Add x-axis
    const xAxis = svg.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x));

    xAxis.select('.domain').remove();
    xAxis.selectAll('line').remove();

    xAxis.selectAll('text')
      .style('font-family', '"Nunito Sans", sans-serif')
      .style('font-size', '10px')
      .style('fill', '#9FA1A4')
      .each((d, i, nodes) => {
        // Same rule as the bar charts: a label may use the distance to its neighbour, minus a gap
        truncateSvgText(nodes[i] as SVGTextElement, Math.max(X_LABEL_MIN_WIDTH, x.step() - X_LABEL_GAP));
      });

    // Draw fill between two series (ROI area between You and Benchmark)
    // This dynamically handles cases where the lines cross (e.g., You > Benchmark or Benchmark > You)
    const _fillBetween = this.fillBetween();
    if (_fillBetween) {
      // Use y0 and y1 dynamically based on which value is higher at each point
      // This ensures proper shading regardless of which line is on top
      const fillBetweenArea = d3.area<AreaLineChartDataPoint>()
        .x(d => x(String(d[_xAxisKey])) || 0)
        .y0(d => {
          const topVal = Number(d[_fillBetween.topKey]);
          const bottomVal = Number(d[_fillBetween.bottomKey]);
          // y0 is the lower y position (higher value since y is inverted)
          return y(Math.min(topVal, bottomVal));
        })
        .y1(d => {
          const topVal = Number(d[_fillBetween.topKey]);
          const bottomVal = Number(d[_fillBetween.bottomKey]);
          // y1 is the higher y position (lower value since y is inverted)
          return y(Math.max(topVal, bottomVal));
        })
        .curve(d3.curveMonotoneX);

      svg.append('path')
        .datum(_data)
        .attr('fill', _fillBetween.color)
        .attr('fill-opacity', _fillBetween.fillOpacity ?? 0.3)
        .attr('d', fillBetweenArea);
    }

    // Draw areas and lines for each series
    _series.forEach(seriesItem => {
      const curveType = seriesItem.lineType === 'straight' ? d3.curveLinear : d3.curveMonotoneX;

      // Create area generator for this series
      const seriesArea = d3.area<AreaLineChartDataPoint>()
        .x(d => x(String(d[_xAxisKey])) || 0)
        .y0(height)
        .y1(d => y(Number(d[seriesItem.key])))
        .curve(curveType);

      // Create line generator for this series
      const seriesLine = d3.line<AreaLineChartDataPoint>()
        .x(d => x(String(d[_xAxisKey])) || 0)
        .y(d => y(Number(d[seriesItem.key])))
        .curve(curveType);

      // Draw filled area (only if fillOpacity > 0)
      if (_showFill && (seriesItem.fillOpacity ?? 0.3) > 0) {
        svg.append('path')
          .datum(_data)
          .attr('fill', seriesItem.color)
          .attr('fill-opacity', seriesItem.fillOpacity ?? 0.3)
          .attr('d', seriesArea);
      }

      // Draw line (only if showLine is true or undefined)
      if (seriesItem.showLine !== false) {
        svg.append('path')
          .datum(_data)
          .attr('fill', 'none')
          .attr('stroke', seriesItem.color)
          .attr('stroke-width', 2)
          .attr('d', seriesLine);
      }

      // Draw points (only if showDots is true and showLine is not false)
      if (_showDots && seriesItem.showLine !== false) {
        svg.selectAll(`.dot-${seriesItem.key}`)
          .data(_data)
          .enter()
          .append('circle')
          .attr('class', `dot-${seriesItem.key}`)
          .attr('cx', d => x(String(d[_xAxisKey])) || 0)
          .attr('cy', d => y(Number(d[seriesItem.key])))
          .attr('r', 4)
          .attr('fill', seriesItem.color)
          .attr('stroke', 'white')
          .attr('stroke-width', 2);
      }
    });

    const xValues = _data.map(d => String(d[_xAxisKey]));
    const xPos = xValues.map(v => x(v) ?? 0);

    // Vertical guide
    const guide = svg.append('line')
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#9FA1A4')
      .attr('stroke-width', 1)
      .style('opacity', 0);

    // Transparent overlay to capture mouse
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'transparent')
      .on('mouseenter', () => {
        tooltip.style('opacity', 1);
        guide.style('opacity', 1);
      })
      .on('mouseleave', () => {
        tooltip.style('opacity', 0);
        guide.style('opacity', 0);
      })
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event, svg.node() as any);

        let idx = 0, best = Infinity;
        for (let i = 0; i < xPos.length; i++) {
          const d = Math.abs(mx - xPos[i]);
          if (d < best) { best = d; idx = i; }
        }
        const datum = _data[idx];
        const label = String(datum[_xAxisKey]);

        const rows = _series
          .filter(s => s.showInTooltip !== false)
          .map(s => {
            const val = Number(datum[s.key]);
            const name = s.label ?? s.key;
            const text = isNaN(val) ? '-' : _tooltipFormat ? _tooltipFormat(val) : `R${val.toLocaleString()}`;
            return `
              <div style="margin-top:4px;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;
                             background:${s.color};margin-right:6px;"></span>
                <span style="color:#9FA1A4;font-size:12px;font-weight: 700;">${name}: ${text}</span>
              </div>`;
          }).join('');

        tooltip.html(`
          <div style="font-weight:700;margin-bottom:4px;color:#4D4749;">${label}</div>
          ${rows}
        `);

        const [cx, cy] = d3.pointer(event, element);
        const th = (tooltip.node() as HTMLElement)?.offsetHeight ?? 0;
        tooltip.style('left', (cx + 10) + 'px')
               .style('top', (cy - th - 10) + 'px');

        guide.attr('x1', xPos[idx]).attr('x2', xPos[idx]);
      });
  }
}